import { rem } from '@mantine/core';
import type { AiDigestItem } from '@reditelji/shared';
import { useAiDigest, useAiMutations } from '../api/ai';
import { useEvent } from '../api/hooks';
import { useSession } from '../api/session';
import { useDeepLinks } from '../lib/deepLinks';
import { CARD, MONO } from '../volunteers/ui';
import { AiPill, refreshedAt, SMALL_BTN } from './AiBits';

const TILES = [
  { key: 'open', label: 'nepokritih izmen', bg: 'oklch(0.965 0.03 25)', bd: 'oklch(0.88 0.05 25)', fg: 'oklch(0.44 0.11 25)' },
  { key: 'pending', label: 'čaka potrditev', bg: 'oklch(0.975 0.03 75)', bd: 'oklch(0.87 0.06 75)', fg: 'oklch(0.38 0.07 75)' },
  { key: 'noBriefing', label: 'brez poslanih navodil', bg: 'oklch(0.955 0.03 245)', bd: 'oklch(0.86 0.05 245)', fg: 'oklch(0.38 0.09 245)' },
  { key: 'needsAction', label: 'pogovorov potrebuje akcijo', bg: 'oklch(0.97 0.005 95)', bd: 'oklch(0.92 0.006 95)', fg: 'oklch(0.4 0.012 90)' },
] as const;

const DOT: Record<AiDigestItem['tone'], string> = {
  red: 'oklch(0.58 0.13 25)',
  amber: 'oklch(0.68 0.11 75)',
  blue: 'oklch(0.55 0.11 245)',
  neutral: 'oklch(0.6 0.012 90)',
};

/**
 * "Dnevni povzetek" — shown on the Prostovoljci empty state and on Pregled.
 * The digest is generated on demand (»Osveži«) and cached server-side; items
 * deep-link into the screen where the action happens.
 */
export function DailyDigestCard({ footer }: { footer?: React.ReactNode }) {
  // The digest is who wrote, who answered and who was sent what (SPEC §1.1L).
  const canRead = useSession().can('comms:read');
  const event = useEvent();
  const enabled = event.data?.ai.enabled ?? false;
  const digest = useAiDigest();
  const mut = useAiMutations();
  const { openDigestLink } = useDeepLinks();
  const d = digest.data ?? null;
  const busy = mut.refreshDigest.isPending;
  if (!canRead) return null;

  return (
    <div style={{ ...CARD, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 13, flexWrap: 'wrap' }}>
        <span style={{ fontSize: rem(14), fontWeight: 700 }}>Dnevni povzetek</span>
        <AiPill>{enabled ? 'Gemini · dnevni pregled' : 'AI izklopljen'}</AiPill>
        <div style={{ flex: 1 }} />
        {d ? <span style={{ fontSize: rem(12), color: 'oklch(0.6 0.012 90)' }}>{refreshedAt(d.createdAt)}</span> : null}
        <button
          onClick={() => mut.refreshDigest.mutate()}
          disabled={!enabled || busy}
          title={enabled ? undefined : 'AI pomočnik ni vklopljen (AI_ENABLED)'}
          style={{ ...SMALL_BTN, cursor: enabled && !busy ? 'pointer' : 'default', opacity: enabled ? 1 : 0.6 }}
        >
          {busy ? 'Pripravljam povzetek…' : 'Osveži'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {TILES.map((t) => (
          <div
            key={t.key}
            style={{ flex: 1, minWidth: 108, padding: '10px 11px', borderRadius: 9, background: t.bg, border: `1px solid ${t.bd}` }}
          >
            <div style={{ fontFamily: MONO, fontSize: rem(18), fontWeight: 700, color: t.fg }}>{d ? d.counts[t.key] : '–'}</div>
            <div style={{ fontSize: rem(11.5), color: 'oklch(0.5 0.012 90)', lineHeight: 1.3, marginTop: 2 }}>{t.label}</div>
          </div>
        ))}
      </div>

      {d && d.items.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {d.items.map((it, i) => (
            <button
              key={i}
              onClick={() => openDigestLink(it.link)}
              disabled={!it.link}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 9,
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid oklch(0.94 0.005 95)',
                background: 'oklch(1 0 0)',
                cursor: it.link ? 'pointer' : 'default',
                textAlign: 'left',
                font: 'inherit',
                width: '100%',
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: DOT[it.tone], marginTop: 5, flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: rem(13.5), color: 'oklch(0.28 0.012 90)', lineHeight: 1.4 }}>{it.text}</span>
                {it.meta ? (
                  <span style={{ display: 'block', fontSize: rem(11.5), color: 'oklch(0.58 0.012 90)', marginTop: 1 }}>{it.meta}</span>
                ) : null}
              </span>
              {it.link ? <span style={{ fontSize: rem(12), color: 'oklch(0.5 0.09 245)', flexShrink: 0 }}>odpri ›</span> : null}
            </button>
          ))}
        </div>
      ) : (
        <div
          style={{
            padding: '14px 14px',
            borderRadius: 9,
            border: '1px dashed oklch(0.9 0.02 285)',
            background: 'oklch(0.99 0.005 285)',
            fontSize: rem(13),
            color: 'oklch(0.5 0.03 285)',
            lineHeight: 1.5,
          }}
        >
          {digest.isPending
            ? 'Nalagam povzetek…'
            : !enabled
              ? 'AI pomočnik ni vklopljen — povzetek bo na voljo, ko bo nastavljen GEMINI_API_KEY in AI_ENABLED=true.'
              : d
                ? 'Nič odprtega — vse zadeve so urejene.'
                : 'Še ni povzetka — klikni Osveži.'}
        </div>
      )}
      {footer}
    </div>
  );
}
